import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import axiosClient from 'http/axios-client';
import { filterTypes } from '../../basic/AdvancedFilters';
import { getFullRegistrationStatus } from '../consents/ConsentsTable';
import AppTable from '../../basic/AppTable';
import AppLoader from '../../basic/AppLoader';
import { useItems } from '../../../hooks/atoms/items-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';

const UpgradedKitsTable = () => {
  const [ filterQuery, setFilterQuery ] = useState({ });

  const [ downloadLoading, setDownloadLoading ] = useState(false);
  const [ downloadAllLoading, setDownloadAllLoading ] = useState(false);

  const {
    items: kits,
    itemsLoading: kitsLoading,
    pagination,
    refetch,
  } = useItems(CollectionKeys.REGISTRATION_KITS, { filterQuery: {
    ...filterQuery,
    _where: [
      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
    'serial_number_id.ancestor_null': false,
  } });

  const downloadData = async (amount) => {
    let theKits = [];

    if ('all' === amount) {
      setDownloadAllLoading(true);
      theKits = await axiosClient.get('registration-kits', { params: { _limit: -1,
        'serial_number_id.ancestor_null': false,
        _where: [
          { _or: [
            [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
            [{ 'serial_number_id.report_payment_status_eq': 'free' }],
          ] },
        ] } });
    } else {
      setDownloadLoading(true);
      theKits = kits;
    }

    if (null == theKits || 0 === theKits.length) {
      return;
    }
    const data = await Promise.all(theKits.map(async (kit) => {
      const upgradedFrom = await axiosClient.get(CollectionKeys.SERIAL_NUMBER, {
        params: {
          _limit: 1,
          id: kit.serial_number_id.ancestor,
        },
      });

      return {
        'Serial Number': kit.serial_number_id.serial,
        LastName: null == kit.child_id ? `${ kit.customer_id?.family_name }` : kit.child_id?.family_name,
        FirstName: null == kit.child_id ? `${ kit.customer_id?.name }` : kit.child_id?.name,
        'Doctor full name': null != kit.doctor ? `${ kit.doctor?.name } ${ kit.doctor?.family_name }` : '',
        'Thematic Package': kit.thematic_package.thematic_category_id.name,
        'Upgraded from': upgradedFrom[0]?.serial || ' ',
        'PDF Available': kit.pdf_available,
        'Dynamic Report Available': kit.dynamic_report_available,
        'Registration done date': kit?.metadata?.registrationDoneDate,
        'Registration Status': getFullRegistrationStatus(kit),
        'Report paid - Kit paid': `${ kit?.serial_number_id?.report_payment_status ?? 'none' } - ${ kit?.serial_number_id?.kit_payment_status ?? 'none' }`,
        'Payment Amount': kit.metadata?.cashAmount,
        'Report language': 3 === kit?.locale ? 'en-US' : 2 === kit?.locale ? 'el-GR' : '' || '',
        MobileNumber: null == kit.child_id ? `${ kit.customer_id?.phone_number }` : kit.child_id?.phone_number,
        LandlineNumber: null == kit.child_id ? `${ kit.customer_id?.house_number }` : kit.child_id?.house_number,
      };
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = fitToColumn(data);

    function fitToColumn(arrayOfArray) {
      return Object.keys(arrayOfArray[0]).map((key) => {
        return { wch: key.length };
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'Upgraded Kits.xlsx');

    if ('all' === amount) {
      setDownloadAllLoading(false);
    } else {
      setDownloadLoading(false);
    }
  };

  if (kitsLoading) {
    return <AppLoader />;
  }

  const tableColumns = [
    { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
  ];

  tableColumns.push({ id: 10,
    label: 'Last name',
    field: 'locale',
    customRenderer({ row }) {
      return (
        <>
          {
            null == row.child_id ? `${ row.customer_id?.family_name }` : row.child_id?.family_name
          }
        </>
      );
    } });
  tableColumns.push({ id: 9,
    label: 'Name',
    field: 'locale',
    customRenderer({ row }) {
      return (
        <>
          {
            null == row.child_id ? `${ row.customer_id?.name }` : row.child_id?.name
          }
        </>
      );
    } });
  tableColumns.push({ id: 277939,
    label: 'Doctor full name',
    field: 'locale',
    customRenderer({ row }) {
      return (
        <>
          {
            null != row.doctor ? `${ row.doctor?.name } ${ row.doctor?.family_name }` : ''
          }
        </>
      );
    } });

  tableColumns.push(
    { id: 2, label: 'Thematic Package', field: 'thematic_package.thematic_category_id.name', noSort: true },

    { id: 237939,
      label: 'Upgraded from',
      field: 'upgraded_from',
      noSort: true,
      customRenderer({ row }) {
        return (
          <>
            {row.serial_number_id?.ancestor?.serial }
          </>
        );
      } },

    { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true, noSort: true },
    { id: 4, label: 'PDF Available', field: 'pdf_available', doneOrButton: true, noSort: true },
    { id: 7, label: 'Dynamic Report Available', field: 'dynamic_report_available', doneOrButton: true, noSort: true },
  );

  tableColumns.push({ id: 8, label: 'Registration done date', field: 'metadata.registrationDoneDate', isDate: true, noSort: true });
  tableColumns.push({ id: 98,
    label: 'Registration Status',
    field: 'registrationStatus',
    noSort: true,
    customRenderer({ row }) {
      return (
        <>
          {
            getFullRegistrationStatus(row)
          }
        </>
      );
    } });

  tableColumns.push({ id: 19,
    label: 'Payment Status',
    field: 'metadata.cashStatus',
    isCashStatus: true,
    noSort: true });

  tableColumns.push({ id: 20, label: 'Payment Amount', field: 'metadata.cashAmount', noSort: true });
  tableColumns.push({ id: 171559,
    label: 'Report language',
    field: 'locale',
    customRenderer({ row }) {
      return (
        <>
          {
            3 === row?.locale ? 'en-US' : 2 === row?.locale ? 'el-GR' : '' || ''
          }
        </>
      );
    } });

  tableColumns.push({ id: 14, label: 'Email', field: 'customer_id.email' });
  tableColumns.push({ id: 15,
    label: 'Mobile number',
    field: 'customer_id.phone_number',
    customRenderer({ row }) {
      return (
        <>
          {
            null == row.child_id ? `${ row.customer_id?.phone_number }` : row.child_id?.phone_number
          }
        </>
      );
    } });
  tableColumns.push({ id: 16,
    label: 'Landline number',
    field: 'customer_id.house_number',
    customRenderer({ row }) {
      return (
        <>
          {
            null == row.child_id ? `${ row.customer_id?.house_number }` : row.child_id?.house_number
          }
        </>
      );
    } });

  return (
    <>
      <LoadingButtonNoGrid
        variant='contained'
        loading={ downloadLoading }
        label='Download'
        onClick={ () => { downloadData('current'); } }
      />

      <LoadingButtonNoGrid
        variant='contained'
        style={ { marginLeft: '15px' } }
        loading={ downloadAllLoading }
        label='Download all'
        onClick={ () => { downloadData('all'); } }
      />

      <AppTable
        columns={ tableColumns }
        data={ kits }
        loading={ kitsLoading }
        paginationProps={ pagination }
        refetch={ refetch }
        advancedFiltersProps={ {
          properties: [
            {
              value: 'serial_number_id.serial',
              label: 'Serial Number',
              type: filterTypes.FREE_TEXT,
            },
            {
              value: 'thematic_package.thematic_category_id.name',
              label: 'Thematic Package',
              type: filterTypes.CATEGORY,
              options: [
                { value: '3-in-1', label: '3-in-1' },
                { value: 'Biological Age', label: 'Biological Age' },
                { value: 'Cardio Health', label: 'Cardio Health' },
                { value: 'NutriGenetix', label: 'NutriGenetix' },
                { value: 'Nutrition', label: 'Nutrition' },
                { value: 'PGx', label: 'PGx' },
                { value: 'Sports', label: 'Sports' },
                { value: 'Vitamins', label: 'Vitamins' },
              ],
            },
          ],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }
      />
    </>
  );
};

export default UpgradedKitsTable;

